import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { RootLayout } from './layouts/RootLayout'
import { ProtectedLayout } from './layouts/ProtectedLayout'
import { LoadingScreen } from '@/components/LoadingScreen'

// Lazy load all route components for code splitting
const HomePage = lazy(() => import('@/features/challenges/pages/HomePage').then(m => ({ default: m.HomePage })))

const ChallengeSelectionPage = lazy(() => import('@/features/challenges/pages/ChallengeSelectionPage').then(m => ({ default: m.ChallengeSelectionPage })))
const ChallengeFrequencyPage = lazy(() => import('@/features/challenges/pages/ChallengeFrequencyPage').then(m => ({ default: m.ChallengeFrequencyPage })))
const ChallengeSuccessPage = lazy(() => import('@/features/challenges/pages/ChallengeSuccessPage').then(m => ({ default: m.ChallengeSuccessPage })))
const ChallengeDetailsPage = lazy(() => import('@/features/challenges/pages/ChallengeDetailsPage').then(m => ({ default: m.ChallengeDetailsPage })))
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage').then(m => ({ default: m.LoginPage })))
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage').then(m => ({ default: m.RegisterPage })))
const ProfilePage = lazy(() => import('@/features/auth/pages/ProfilePage').then(m => ({ default: m.ProfilePage })))
const AchievementsPage = lazy(() => import('@/features/achievements/pages/AchievementsPage').then(m => ({ default: m.AchievementsPage })))
const JoinChallengePage = lazy(() => import('@/features/sharing/pages/JoinChallengePage').then(m => ({ default: m.JoinChallengePage })))
const NotFoundPage = lazy(() => import('@/components/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const UnifiedNotificationDebugPage = lazy(() => import('@/pages/UnifiedNotificationDebugPage').then(m => ({ default: m.UnifiedNotificationDebugPage })))
const FCMDebugPage = lazy(() => import('@/pages/FCMDebugPage').then(m => ({ default: m.FCMDebugPage })))
const ScheduledNotificationDebugPage = lazy(() => import('@/pages/ScheduledNotificationDebugPage').then(m => ({ default: m.default })))

// Wrap lazy components with Suspense
const LazyComponent = ({ Component }: { Component: React.LazyExoticComponent<React.ComponentType<any>> }) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component />
  </Suspense>
)

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <LazyComponent Component={HomePage} />,
      },
      {
        path: 'login',
        element: <LazyComponent Component={LoginPage} />,
      },
      {
        path: 'register',
        element: <LazyComponent Component={RegisterPage} />,
      },
      {
        path: 'join/:shareToken',
        element: <LazyComponent Component={JoinChallengePage} />,
      },
      {
        path: 'challenges/new',
        element: <LazyComponent Component={ChallengeSelectionPage} />,
      },
      {
        path: 'challenges/new/selection',
        element: <LazyComponent Component={ChallengeSelectionPage} />,
      },
      {
        path: 'challenges/new/frequency',
        element: <LazyComponent Component={ChallengeFrequencyPage} />,
      },
      {
        path: 'challenges/new/success',
        element: <LazyComponent Component={ChallengeSuccessPage} />,
      },
      {
        path: 'challenges/:challengeId',
        element: <LazyComponent Component={ChallengeDetailsPage} />,
      },
      {
        path: 'unified-debug-notifications',
        element: <LazyComponent Component={UnifiedNotificationDebugPage} />,
      },
      {
        path: 'fcm-debug',
        element: <LazyComponent Component={FCMDebugPage} />,
      },
      {
        path: 'scheduled-notification-debug',
        element: <LazyComponent Component={ScheduledNotificationDebugPage} />,
      },
      {
        element: <ProtectedLayout />,
        children: [
          {
            path: 'profile',
            element: <LazyComponent Component={ProfilePage} />,
          },
          {
            path: 'achievements',
            element: <LazyComponent Component={AchievementsPage} />,
          },
        ],
      },
      {
        path: '*',
        element: <LazyComponent Component={NotFoundPage} />,
      },
    ],
  },
], {
  future: {
    v7_startTransition: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
    v7_viewTransition: true,
  } as any,
})

export function AppRouter() {
  return (
    <RouterProvider 
      router={router} 
      future={{ 
        v7_startTransition: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true,
        v7_viewTransition: true,
      } as any} 
    />
  )
}